import React, { useMemo, useState } from "react";
import { Compass, MapPin, Send, Sun, Thermometer, TrendingDown, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BUILDING_STANDARDS, ENERGY_SOURCES, EU_COUNTRIES, HEAT_EMITTERS, MICRO_CLIMATES, ORIENTATIONS,
} from "../config/euEnergyData";

const chooseCapacity = (load) => {
  const sizes = [3, 5, 6, 8, 10, 12, 14, 16, 20, 26, 30, 35, 40, 50, 60];
  const match = sizes.find((size) => size >= load * 1.05);
  return match ? `${match} kW` : `Kaskade ab ${Math.ceil(load / 5) * 5} kW`;
};

export default function EnergyCalculator() {
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState("AT");
  const [microClimate, setMicroClimate] = useState("normal");
  const [standard, setStandard] = useState("neubau");
  const [area, setArea] = useState(140);
  const [occupants, setOccupants] = useState(4);
  const [emitter, setEmitter] = useState("floor");
  const [source, setSource] = useState("gas");
  const [tariff, setTariff] = useState(0.30);
  const [annualPower, setAnnualPower] = useState(4500);
  const [roofArea, setRoofArea] = useState(40);
  const [orientation, setOrientation] = useState("s");

  const selectCountry = (code) => {
    const next = EU_COUNTRIES.find((entry) => entry.code === code);
    setCountryCode(code);
    setTariff(next.defaultTariff);
  };

  const result = useMemo(() => {
    const country = EU_COUNTRIES.find((entry) => entry.code === countryCode);
    const climate = MICRO_CLIMATES.find((entry) => entry.key === microClimate);
    const building = BUILDING_STANDARDS.find((entry) => entry.key === standard);
    const heatSystem = HEAT_EMITTERS.find((entry) => entry.key === emitter);
    const currentSource = ENERGY_SOURCES.find((entry) => entry.key === source);
    const roof = ORIENTATIONS.find((entry) => entry.key === orientation);
    const climateLoad = country.heatFactor * climate.factor;
    const spaceHeatLoad = building.watts * area * climateLoad / 1000;
    const heatLoad = spaceHeatLoad + occupants * 0.12;
    const annualThermal = spaceHeatLoad * 1800 + occupants * 650;
    const scop = Math.max(2.4, heatSystem.scop / Math.pow(climateLoad, 0.35));
    const currentEnergyUse = source === "waermepumpe"
      ? annualThermal / currentSource.efficiency
      : annualThermal / currentSource.efficiency;
    const currentCost = currentEnergyUse * tariff * currentSource.priceFactor;
    const wpElectricity = annualThermal / scop;
    const wpCost = wpElectricity * tariff;
    const kwpPossible = roofArea / 5.3;
    const annualYield = kwpPossible * country.pvYield * roof.factor;
    const ownUseRatio = Math.min(0.58, 0.35 + wpElectricity / Math.max(annualYield, 1) * 0.12);
    const ownUseKwh = Math.min(annualYield, (annualPower + wpElectricity) * ownUseRatio);
    return {
      country: country.label, designTemp: country.designTemp, heatLoad: heatLoad.toFixed(1),
      annualKwh: Math.round(annualThermal), scop: scop.toFixed(1), wpTarget: chooseCapacity(heatLoad),
      currentCost: Math.round(currentCost), wpCost: Math.round(wpCost),
      saving: Math.max(0, Math.round(currentCost - wpCost)), wpElectricity: Math.round(wpElectricity),
      kwpPossible: kwpPossible.toFixed(1), annualYield: Math.round(annualYield),
      ownUseKwh: Math.round(ownUseKwh), batteryRecommended: Math.max(5, Math.round((annualPower + wpElectricity) / 365 / 2)),
      annualSavingPV: Math.round(ownUseKwh * tariff), flowTemperature: heatSystem.flow,
    };
  }, [countryCode, microClimate, standard, area, occupants, emitter, source, tariff, annualPower, roofArea, orientation]);

  const sendToContact = () => {
    const payload = {
      source: "energie-rechner",
      building: { countryCode, country: result.country, microClimate, standard, area, occupants, emitter, source, tariff, annualPower, roofArea, orientation },
      result,
    };
    try { sessionStorage.setItem("eco_prefill", JSON.stringify(payload)); } catch {}
    navigate("/kontakt");
  };

  return (
    <div className="surface-panel overflow-hidden" data-testid="energy-calculator">
      <div className="grid grid-cols-1 lg:grid-cols-5">
        <div className="lg:col-span-3 p-6 md:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-white/10">
          <SectionTitle number="01" icon={<MapPin size={13}/>} title="EU-Standort & Klima"/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            <label className="text-xs text-white/60">EU-Land
              <select value={countryCode} onChange={(e) => selectCountry(e.target.value)} className="input-field mt-2" data-testid="calc-country-select">
                {EU_COUNTRIES.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-white/60">Mikrolage
              <select value={microClimate} onChange={(e) => setMicroClimate(e.target.value)} className="input-field mt-2" data-testid="calc-climate-select">
                {MICRO_CLIMATES.map((entry) => <option key={entry.key} value={entry.key}>{entry.label}</option>)}
              </select>
            </label>
          </div>

          <SectionTitle number="02" icon={<Thermometer size={13}/>} title="Gebäude & Wärmeabgabe"/>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
            {BUILDING_STANDARDS.map((entry) => <Choice key={entry.key} active={standard === entry.key} onClick={() => setStandard(entry.key)} testid={`calc-standard-${entry.key}`} label={entry.label} sub={`${entry.sub} · ${entry.watts} W/m²`}/>) }
          </div>
          <Slider label="Beheizte Fläche" value={`${area} m²`} min={40} max={2500} step={10} state={area} setState={setArea} testid="calc-area-slider"/>
          <Slider label={<span className="inline-flex items-center gap-2"><Users size={13}/> Personen / Warmwasser</span>} value={`${occupants}`} min={1} max={30} step={1} state={occupants} setState={setOccupants} testid="calc-occupants-slider"/>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-8">
            {HEAT_EMITTERS.map((entry) => <Choice key={entry.key} active={emitter === entry.key} onClick={() => setEmitter(entry.key)} testid={`calc-emitter-${entry.key}`} label={entry.label} sub={entry.flow}/>) }
          </div>

          <SectionTitle number="03" icon={<TrendingDown size={13}/>} title="Bestand & Energiepreis"/>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            {ENERGY_SOURCES.map((entry) => <Choice key={entry.key} active={source === entry.key} onClick={() => setSource(entry.key)} testid={`calc-source-${entry.key}`} label={entry.label}/>) }
          </div>
          <label className="block text-xs text-white/60 mb-8">Stromtarif in EUR/kWh
            <input type="number" min="0.05" max="1" step="0.01" value={tariff} onChange={(e) => setTariff(Math.max(0.05, Number(e.target.value)))} className="input-field mt-2 max-w-[220px]" data-testid="calc-tariff-input"/>
          </label>

          <SectionTitle number="04" icon={<Sun size={13}/>} title="PV-Potenzial"/>
          <Slider label="Jahresstromverbrauch" value={`${annualPower.toLocaleString("de-DE")} kWh`} min={1500} max={50000} step={500} state={annualPower} setState={setAnnualPower} testid="calc-power-slider"/>
          <Slider label="Nutzbare Dachfläche" value={`${roofArea} m²`} min={10} max={500} step={5} state={roofArea} setState={setRoofArea} testid="calc-roof-slider"/>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {ORIENTATIONS.map((entry) => <Choice key={entry.key} active={orientation === entry.key} onClick={() => setOrientation(entry.key)} testid={`calc-orient-${entry.key}`} label={entry.label}/>) }
          </div>
        </div>

        <div className="lg:col-span-2 p-6 md:p-8 lg:p-10 bg-[#0A1F38]/85" data-testid="calc-results">
          <div className="eyebrow mb-6 text-[#5FA637]">EU-Auslegung · Orientierung</div>
          <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-6">
            <Metric label="Land" value={result.country} testid="calc-country-result"/>
            <Metric label="Norm-Außentemperatur" value={`${result.designTemp} °C`} testid="calc-design-temp"/>
            <Metric label="Heizlast" value={`${result.heatLoad} kW`} testid="calc-heatload"/>
            <Metric label="Empfohlene Leistung" value={result.wpTarget} testid="calc-target"/>
            <Metric label="Jahreswärme" value={`${result.annualKwh.toLocaleString("de-DE")} kWh`} testid="calc-kwh"/>
            <Metric label="Erwarteter SCOP" value={result.scop} testid="calc-scop"/>
          </div>
          <div className="space-y-4">
            <ResultRow label="Aktuelle Heizkosten" value={`€ ${result.currentCost.toLocaleString("de-DE")} / Jahr`} testid="calc-current" muted/>
            <ResultRow label="Mit Wärmepumpe" value={`€ ${result.wpCost.toLocaleString("de-DE")} / Jahr`} testid="calc-wp" sub={`${result.wpElectricity.toLocaleString("de-DE")} kWh Strom · ${result.flowTemperature} Vorlauf`}/>
            <div className="p-5 border border-[#5FA637] bg-[#5FA637]/5">
              <div className="eyebrow text-[#5FA637] mb-1">Mögliche Ersparnis / Jahr</div>
              <div className="font-display text-3xl" data-testid="calc-saving">€ {result.saving.toLocaleString("de-DE")}</div>
            </div>
            <div className="pt-2 border-t border-white/10"/>
            <ResultRow label={<><Sun size={11} className="inline mr-1"/>PV-Potenzial</>} value={`${result.kwpPossible} kWp`} testid="calc-pv-kwp"/>
            <ResultRow label="PV-Jahresertrag" value={`${result.annualYield.toLocaleString("de-DE")} kWh`} testid="calc-pv-yield"/>
            <ResultRow label="Davon Eigenverbrauch" value={`${result.ownUseKwh.toLocaleString("de-DE")} kWh`} testid="calc-pv-own"/>
            <ResultRow label="Speicher-Empfehlung" value={`${result.batteryRecommended} kWh`} testid="calc-battery" sub="Orientierungswert"/>
            <div className="p-4 border border-white/15">
              <div className="eyebrow text-[#5FA637] mb-1">PV-Ersparnis / Jahr</div>
              <div className="font-display text-2xl" data-testid="calc-pv-saving">€ {result.annualSavingPV.toLocaleString("de-DE")}</div>
            </div>
            <button onClick={sendToContact} className="btn-primary w-full justify-center mt-6" data-testid="calc-to-contact">Projektanfrage mit Daten <Send size={14}/></button>
            <p className="text-[11px] text-white/40 italic text-center leading-relaxed">EU-weite Orientierungsrechnung. Eine normgerechte Heizlast- und Schallauslegung erfolgt nach Prüfung des konkreten Standorts.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const SectionTitle = ({ number, icon, title }) => <div className="eyebrow mb-4 text-[#5FA637] flex items-center gap-2">{number} · {icon} {title}</div>;

function Choice({ active, onClick, testid, label, sub }) {
  return <button type="button" onClick={onClick} data-testid={testid} className={`technical-card min-h-[58px] p-3 text-left ${active ? "border-[#5FA637] bg-[#5FA637]/10" : "border-white/15"}`}><span className="block text-xs font-semibold">{label}</span>{sub && <span className="block text-[9px] text-white/50 mt-1">{sub}</span>}</button>;
}

function Slider({ label, value, min, max, step, state, setState, testid }) {
  return <div className="mb-6"><div className="flex items-center justify-between gap-4 mb-2"><span className="text-xs text-white/60">{label}</span><span className="text-sm tabular-nums" data-testid={`${testid}-value`}>{value}</span></div><input type="range" min={min} max={max} step={step} value={state} onChange={(e) => setState(Number(e.target.value))} className="w-full accent-[#5FA637]" data-testid={testid}/></div>;
}

const Metric = ({ label, value, testid }) => <div className="bg-[#112640] p-4 min-w-0"><div className="text-[9px] uppercase tracking-[0.1em] text-white/45 mb-1">{label}</div><div className="font-display text-base break-words" data-testid={testid}>{value}</div></div>;

function ResultRow({ label, value, sub, muted, testid }) {
  return <div><div className="text-[10px] text-white/50 uppercase tracking-[0.1em] mb-1">{label}</div><div className={`font-display text-xl ${muted ? "text-white/60" : ""}`} data-testid={testid}>{value}</div>{sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}</div>;
}